'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { FIRST_STEP, STEP_MAP, TOTAL_STEPS } from './constants';

/**
 * Decides whether a step may be entered. Registered by `StepAccessGuard` (which
 * lives inside `SchedulerProvider` and therefore can see the customer's data);
 * this provider sits above it in the tree, so the guard is injected rather than
 * imported. When no guard is registered — the client quote portal, isolated unit
 * tests — navigation is unrestricted.
 */
export type StepGuard = (targetStep: number) => boolean;

type StepNavigationContextType = {
  activeStep: number;
  direction: 'forward' | 'backward';
  /**
   * Jump to a step. Forward jumps are refused when the registered guard says the
   * customer has not finished the steps in between — this is the
   * catch-all behind the top stepper, the summary bar and `?step=` deep links.
   */
  setActiveStep: (step: number) => void;
  /**
   * Advance exactly one step. Deliberately NOT guarded: this is the API the
   * per-step "Continue" buttons use, and each of those already refuses to fire
   * until its own step validates. Routing it through the guard would read
   * pre-update context in handlers that save data and advance in one go (e.g.
   * `setContact(...); nextStep()`) and wrongly block the customer.
   */
  nextStep: () => void;
  previousStep: () => void;
  registerStepGuard: (guard: StepGuard | null) => void;
};

const StepNavigationContext = createContext<
  StepNavigationContextType | undefined
>(undefined);

export const StepNavigationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const searchParams = useSearchParams();

  // NOTE: the step is intentionally not mirrored back into the URL — that
  // rewrite was removed because it fought with draft resume. `?step=` is still
  // honoured as a *request*: `StepAccessGuard` pulls the customer back to the
  // furthest step their data allows once the scheduler has settled, so a
  // hand-edited URL cannot land them past an unfinished step.
  const stepNameFromUrl = searchParams.get('step') || 'services';
  const stepFromUrl = STEP_MAP[stepNameFromUrl.toLowerCase()] || FIRST_STEP;
  const initialStep =
    stepFromUrl >= FIRST_STEP && stepFromUrl <= TOTAL_STEPS
      ? stepFromUrl
      : FIRST_STEP;

  const [activeStep, setActiveStepState] = useState(initialStep);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const stepGuardRef = useRef<StepGuard | null>(null);

  const registerStepGuard = useCallback((guard: StepGuard | null) => {
    stepGuardRef.current = guard;
  }, []);

  const setActiveStep = useCallback(
    (step: number) => {
      if (step < FIRST_STEP || step > TOTAL_STEPS) return;
      if (step === activeStep) return;

      // Only forward jumps are gated. Going back is always permitted, and the
      // guard is deliberately not consulted for it so a clamp or a Start Over
      // can never be refused.
      if (step > activeStep && stepGuardRef.current?.(step) === false) return;

      setDirection(step > activeStep ? 'forward' : 'backward');
      setActiveStepState(step);
    },
    [activeStep]
  );

  const nextStep = useCallback(() => {
    setDirection('forward');
    setActiveStepState((prev) => Math.min(prev + 1, TOTAL_STEPS));
  }, []);

  const previousStep = useCallback(() => {
    setDirection('backward');
    setActiveStepState((prev) => Math.max(prev - 1, FIRST_STEP));
  }, []);

  const value = useMemo(
    () => ({
      activeStep,
      direction,
      setActiveStep,
      nextStep,
      previousStep,
      registerStepGuard,
    }),
    [
      activeStep,
      direction,
      setActiveStep,
      nextStep,
      previousStep,
      registerStepGuard,
    ]
  );

  return (
    <StepNavigationContext.Provider value={value}>
      {children}
    </StepNavigationContext.Provider>
  );
};

export const useStepNavigation = () => {
  const context = useContext(StepNavigationContext);
  if (!context) {
    throw new Error(
      'useStepNavigation must be used within StepNavigationProvider'
    );
  }
  return context;
};
